import { auth } from '../../auth/appAuth';
import { assignUniqueClientSlug } from '../../shared/clients/companyClientRouting';
import {
  createClient,
  getClient,
  listClients,
  upsertClient,
} from '../../api/companyRecords';

export const CLIENT_STATUS = {
  NEW: 'new',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  VIP: 'vip'
};

export const createClientProfile = async (companyId, clientData) => {
  const fullName = `${clientData.firstName || ''} ${clientData.lastName || ''}`.trim();
  const existingClients = await listClients({ companyId, archived: 'all' });
  const slug = assignUniqueClientSlug(fullName || clientData.name || 'client', existingClients);
  const now = new Date().toISOString();

  const profileData = {
    ...clientData,
    name: fullName || clientData.name || '',
    slug,
    status: CLIENT_STATUS.NEW,
    createdAt: now,
    updatedAt: now,
    loyaltyPoints: 0,
    preferences: {},
    notes: [],
    tags: [],
    totalSpent: 0,
    appointmentCount: 0,
    lastAppointment: null,
    searchName: `${clientData.firstName || ''} ${clientData.lastName || ''}`.toLowerCase()
  };

  return createClient({ companyId, client: profileData });
};

export const updateClientProfile = async (companyId, clientId, updates) => {
  await upsertClient({
    companyId,
    id: clientId,
    client: {
      ...updates,
      updatedAt: new Date().toISOString(),
    },
  });
};

export const addNoteToProfile = async (companyId, clientId, noteText) => {
  const currentUser = auth.currentUser;
  const now = new Date().toISOString();
  const newNote = {
    id: crypto.randomUUID(),
    text: noteText,
    createdAt: now,
    createdBy: currentUser ? currentUser.displayName || currentUser.email : 'Staff Member',
    createdById: currentUser ? currentUser.uid : null,
    updatedAt: now
  };

  const client = await getClient({ companyId, id: clientId });
  const notes = Array.isArray(client?.notes) ? [...client.notes, newNote] : [newNote];
  await upsertClient({
    companyId,
    id: clientId,
    client: { notes, updatedAt: now },
  });
  return newNote;
};

export const editNote = async (companyId, clientId, noteId, newText) => {
  const client = await getClient({ companyId, id: clientId });
  if (!client) {
    throw new Error('Client document not found');
  }

  const now = new Date().toISOString();
  const notes = (client.notes || []).map((note) => (
    note.id === noteId ? { ...note, text: newText, updatedAt: now } : { ...note }
  ));

  await upsertClient({
    companyId,
    id: clientId,
    client: { notes, updatedAt: now },
  });
};

export const deleteNote = async (companyId, clientId, noteToDelete) => {
  const client = await getClient({ companyId, id: clientId });
  if (!client) {
    throw new Error('Client document not found');
  }

  const currentNotes = client.notes || [];
  const updatedNotes = currentNotes.filter((note) => note.id !== noteToDelete.id);
  if (updatedNotes.length === currentNotes.length) return;

  await upsertClient({
    companyId,
    id: clientId,
    client: { notes: updatedNotes, updatedAt: new Date().toISOString() },
  });
};

export const updateClientPreferences = async (companyId, clientId, preferences) => {
  await upsertClient({
    companyId,
    id: clientId,
    client: { preferences, updatedAt: new Date().toISOString() },
  });
};

export const updateClientStatus = async (companyId, clientId, status) => {
  if (!Object.values(CLIENT_STATUS).includes(status)) {
    throw new Error('Invalid client status');
  }

  await upsertClient({
    companyId,
    id: clientId,
    client: { status, updatedAt: new Date().toISOString() },
  });
};
